/**
 * Die Startseite — die Seite ohne Link, seit `W2` die Vorstellung für Verlage (**E135**).
 *
 * **Gestaltet wie die Titelkarte des Erklärfilms** (vom User abgestimmt): Grund BB4, Schrift Inter,
 * links „Flaghex“ mit Unterzeile und Eckdaten, rechts das Brett mit den sechs Fähnrichen und ihren
 * Linien zum Zentrum. Nur diese Seite trägt den Look; Lobby und Spiel bleiben bei **E33** bis **E38**.
 *
 * **Das einzige Feld ist ein Verlagsschlüssel.** Er öffnet die Anleitung samt Film und sonst nichts.
 * Er reist im Körper eines `POST` und steht in keiner Adresse; **ob er gilt, entscheidet der
 * Dienst** (Regel 8) — er setzt dabei das Cookie, ohne das `/anleitung/` nichts ausliefert. Eine
 * öffentliche Partieliste gibt es weiter nicht (§65); wer mitspielen will, braucht einen
 * Einladungslink, und die Fußzeile sagt das.
 *
 * **Das Brett ist `BoardSvg`**, dasselbe wie im Spiel, und kein eingefügtes Bild: eine Änderung an
 * Farben oder Geometrie erreicht die Startseite von selbst.
 */

import '@fontsource-variable/inter';

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultRuleConfig } from '@flaghex/contracts';
import { hex, type Hex, type SectorId } from '@flaghex/engine';

import { useRequest } from '../api/context.js';
import { health, unlockManual } from '../api/endpoints.js';
import { BoardSvg, type BoardPiece } from '../board/BoardSvg.js';
import { hexToPixel, REFERENCE_HEX_SIZE } from '../board/geometry.js';
import { rotateHex, viewSteps } from '../board/orientation.js';
import { PIECE_RADIUS_RATIO, SECTOR_COLORS } from '../board/palette.js';

/** Die Reihenfolge der Sektoren ist gleichgültig; gezeichnet werden alle sechs. */
const SECTORS: readonly SectorId[] = ['blue', 'red', 'yellow', 'green', 'purple', 'orange'];

/**
 * Wo der Fähnrich von Blau steht — die Mitte der äußeren Reihe, wie in Film und Anleitung
 * (`manual/scenes.ts`, `AUFSTELLUNG_BLAU`). Die anderen Sektoren sind dieselbe Stelle, gedreht.
 */
const BLUE_FLAG_BEARER: Hex = hex(-4, 8);

const FLAG_BEARERS: readonly BoardPiece[] = SECTORS.map((sector) => ({
  id: `${sector}-flagBearer`,
  type: 'flagBearer',
  owner: sector,
  sector,
  at: rotateHex(BLUE_FLAG_BEARER, -viewSteps(sector)),
}));

/** Wie weit die Linie vor dem Zentrum endet — am Rand des roten Feldes, nicht in seiner Mitte. */
const LINE_END_RATIO = 1.35;

/**
 * Je Fähnrich eine feine Linie in seiner Farbe zum Zentrum, zum Zentrum hin kräftiger
 * (`docs/anleitung/video/ENTSCHEIDUNGEN.md`, „Linien zum Zentrum“).
 */
function CenterLines() {
  const size = REFERENCE_HEX_SIZE;
  return (
    <>
      <defs>
        {FLAG_BEARERS.map((piece) => {
          const from = hexToPixel(piece.at, size);
          return (
            <linearGradient
              key={piece.sector}
              id={`center-line-${piece.sector}`}
              gradientUnits="userSpaceOnUse"
              x1={from.x}
              y1={from.y}
              x2={0}
              y2={0}
            >
              <stop offset="0" stopColor={SECTOR_COLORS[piece.sector].piece} stopOpacity={0.25} />
              <stop offset="1" stopColor={SECTOR_COLORS[piece.sector].piece} stopOpacity={0.95} />
            </linearGradient>
          );
        })}
      </defs>
      {FLAG_BEARERS.map((piece) => {
        const from = hexToPixel(piece.at, size);
        const length = Math.hypot(from.x, from.y);
        const ux = from.x / length;
        const uy = from.y / length;
        const start = length - size * PIECE_RADIUS_RATIO;
        const end = size * LINE_END_RATIO;
        return (
          <line
            key={piece.sector}
            data-center-line={piece.sector}
            x1={ux * start}
            y1={uy * start}
            x2={ux * end}
            y2={uy * end}
            stroke={`url(#center-line-${piece.sector})`}
            strokeWidth={size * 0.09}
            strokeLinecap="round"
          />
        );
      })}
    </>
  );
}

export interface StartScreenProps {
  /** Öffnet die Anleitung, nachdem der Dienst den Schlüssel angenommen hat. Für Tests hereingegeben. */
  readonly openManual?: () => void;
}

type Outcome = { readonly kind: 'idle' } | { readonly kind: 'busy' } | { readonly kind: 'failed'; readonly message: string };

export function StartScreen({ openManual = () => window.location.assign('/anleitung/') }: StartScreenProps) {
  const { t } = useTranslation(['start', 'errors']);
  const request = useRequest();
  const [key, setKey] = useState('');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const rules = defaultRuleConfig();
  /**
   * **Ein unerreichbarer Dienst ist eine Aussage, kein stummes Feld** (seit Zyklus 10a). Wer den
   * Schlüssel eingibt, während der Dienst neu startet, soll vorher wissen, warum es nicht geht.
   * Ein erreichbarer Dienst bekommt keine Zeile — die Seite stellt ein Spiel vor, keinen Server.
   */
  const [unreachable, setUnreachable] = useState<string | undefined>(undefined);

  useEffect(() => {
    let current = true;
    void health(request).then((result) => {
      if (current && result.kind === 'failure') setUnreachable(t(`errors:${result.code}`));
    });
    return () => {
      current = false;
    };
  }, [request, t]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = key.trim();
    if (trimmed === '' || outcome.kind === 'busy') return;
    setOutcome({ kind: 'busy' });
    void unlockManual(request, trimmed).then((result) => {
      if (result.kind === 'ok') {
        setOutcome({ kind: 'idle' });
        openManual();
        return;
      }
      setOutcome({
        kind: 'failed',
        message: result.code === 'UNAUTHENTICATED' ? t('start:keyInvalid') : t(`errors:${result.code}`),
      });
    });
  };

  return (
    <div className="start-page">
      <main className="start-stage">
        <section className="start-copy">
          <div>
            <h1>{t('start:title')}</h1>
            <p className="start-tagline">{t('start:tagline')}</p>
          </div>
          <form className="start-unlock" onSubmit={submit} noValidate>
            <label htmlFor="publisher-key">{t('start:keyLabel')}</label>
            <div className="start-unlock-row">
              <input
                id="publisher-key"
                name="key"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={t('start:keyPlaceholder')}
                value={key}
                onChange={(event) => setKey(event.target.value)}
                aria-describedby="publisher-key-message"
              />
              <button type="submit" disabled={key.trim() === '' || outcome.kind === 'busy'}>
                {t('start:unlock')}
              </button>
            </div>
            <p
              id="publisher-key-message"
              className={outcome.kind === 'failed' || unreachable !== undefined ? 'start-error' : 'start-hint'}
              role={outcome.kind === 'failed' || unreachable !== undefined ? 'alert' : undefined}
            >
              {outcome.kind === 'failed' ? outcome.message : (unreachable ?? t('start:keyHint'))}
            </p>
          </form>
        </section>
        <div className="start-board" data-start-board>
          <BoardSvg
            sideLength={rules.board.sideLength}
            startZoneRows={rules.startZone.rows}
            pieces={FLAG_BEARERS}
            underlay={<CenterLines />}
            goalLabel={false}
          />
        </div>
      </main>
      <footer className="start-footer">{t('start:noLink')}</footer>
    </div>
  );
}
